/**
 * CamerasConfigPanel — Configuración de canales y analíticas por NVR
 *
 * Modal pantalla completa que muestra todos los canales del NVR con:
 *   - Snapshot en vivo (con botón refresh)
 *   - Nombre editable
 *   - 6 checkboxes de analíticas:
 *       Vehicular (bundle: lpr + vehicle + color + tipo)
 *       Personas (detección + tracking)
 *       Rostros (reconocimiento + watchlist)
 *       Armas (YOLO weapons, alerta crítica)
 *       Merodeo (loitering, configurable segundos)
 *       Perímetro (línea vertical o polígono — alerta crítica)
 *
 * Al guardar: hace PUT al remote_config del EdgeNode del sitio del NVR.
 * El daemon edge recoge el cambio en su próximo heartbeat config-refresh.
 */

import { useCallback, useEffect, useState } from 'react'

// ─── Constantes ──────────────────────────────────────────────────────────────

const ANALYTICS_OPTIONS = [
  { id: 'vehicular', label: 'Vehicular',  desc: 'LPR + color + tipo (auto/camión/bus/moto/bici)' },
  { id: 'personas',  label: 'Personas',   desc: 'Detección + tracking + conteo' },
  { id: 'rostros',   label: 'Rostros',    desc: 'Reconocimiento + watchlist negra' },
  { id: 'armas',     label: 'Armas',      desc: 'Pistola/cuchillo/rifle — ALERTA CRÍTICA', urgent: true },
  { id: 'merodeo',   label: 'Merodeo',    desc: 'Persona detenida >N segundos — ALERTA',  urgent: true },
  { id: 'perimetro', label: 'Perímetro',  desc: 'Cruce de línea o zona protegida — ALERTA CRÍTICA', urgent: true },
]

// ─── Helpers de conversión config ↔ checkboxes ───────────────────────────────

function camToAnalyticsFlags(cam) {
  const ana = cam.analytics || []
  return {
    vehicular: ana.includes('vehicle') || ana.includes('lpr'),
    personas:  ana.includes('person'),
    rostros:   !!cam.face_recognition,
    armas:     !!cam.weapon_detection,
    merodeo:   (cam.loitering_seconds || 0) > 0,
    perimetro: !!cam.protected_line,
  }
}

function flagsToCamConfig(currentCam, flags, extra = {}) {
  const ana = []
  if (flags.vehicular) ana.push('lpr', 'vehicle')
  if (flags.personas)  ana.push('person')

  const next = {
    ...currentCam,
    analytics:        ana,
    face_recognition: flags.rostros,
    weapon_detection: flags.armas,
  }

  // Merodeo: si activo, mantener segundos existentes (default 5)
  if (flags.merodeo) {
    next.loitering_seconds         = extra.loitering_seconds ?? currentCam.loitering_seconds ?? 5
    next.loitering_max_movement_px = currentCam.loitering_max_movement_px ?? 80
  } else {
    next.loitering_seconds = 0
  }

  // Perímetro: si activo, mantener config existente o default línea x_rel=0.5
  if (flags.perimetro) {
    next.protected_line = extra.protected_line ?? currentCam.protected_line ?? {
      mode: 'line', x_rel: 0.5, direction: 'right_to_left',
      labels: ['person', 'car', 'truck', 'bus', 'motorcycle', 'bicycle'],
    }
  } else {
    next.protected_line = null
  }
  return next
}

// ─── CameraCard ──────────────────────────────────────────────────────────────

function CameraCard({ channel, channelName, snapshotUrl, camConfig, onChange }) {
  const [flags, setFlags] = useState(() => camToAnalyticsFlags(camConfig || {}))
  const [name, setName] = useState(channelName || `CH${String(channel).padStart(2, '0')}`)
  const [loitSecs, setLoitSecs] = useState(camConfig?.loitering_seconds || 5)
  const [protLine, setProtLine] = useState(camConfig?.protected_line || {
    mode: 'line', x_rel: 0.5, direction: 'right_to_left',
  })
  const [snapKey, setSnapKey] = useState(Date.now())
  const [snapError, setSnapError] = useState(false)

  // Propagar cualquier cambio al parent (debounced via onChange)
  useEffect(() => {
    const next = flagsToCamConfig(
      { ...(camConfig || {}), channel, name },
      flags,
      { loitering_seconds: loitSecs, protected_line: protLine },
    )
    onChange?.(channel, next)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flags, name, loitSecs, protLine])

  const toggle = (id) => setFlags(f => ({ ...f, [id]: !f[id] }))

  return (
    <div style={{
      background: 'var(--bg-surface)', border: '1px solid var(--border)',
      borderRadius: 8, overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Snapshot + refresh + nombre canal */}
      <div style={{ position: 'relative', background: '#000', aspectRatio: '16/9' }}>
        {!snapError && snapshotUrl ? (
          <img
            key={snapKey}
            src={`${snapshotUrl}&_=${snapKey}`}
            alt={`CH${channel}`}
            onError={() => setSnapError(true)}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#555', fontSize: 12,
          }}>
            sin snapshot
          </div>
        )}
        <div style={{
          position: 'absolute', top: 6, left: 8,
          background: 'rgba(0,0,0,0.7)', color: '#fff',
          padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700,
        }}>CH{String(channel).padStart(2, '0')}</div>
        <button
          onClick={() => { setSnapError(false); setSnapKey(Date.now()) }}
          title="Refresh snapshot"
          style={{
            position: 'absolute', top: 6, right: 6,
            background: 'rgba(0,0,0,0.7)', color: '#fff', border: 'none',
            padding: '3px 8px', borderRadius: 4, fontSize: 11, cursor: 'pointer',
          }}>↻</button>
      </div>

      {/* Nombre editable */}
      <div style={{ padding: '8px 10px 4px', borderBottom: '1px solid var(--border)' }}>
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder={`CH${channel}`}
          style={{
            width: '100%', padding: '5px 8px', fontSize: 12,
            background: 'var(--bg-root)', color: 'var(--text-primary)',
            border: '1px solid var(--border)', borderRadius: 4,
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Checkboxes analíticas */}
      <div style={{ padding: '6px 10px', display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
        {ANALYTICS_OPTIONS.map(opt => {
          const active = flags[opt.id]
          return (
            <label
              key={opt.id}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 6,
                padding: '4px 6px', borderRadius: 4, cursor: 'pointer',
                background: active ? (opt.urgent ? 'rgba(239,68,68,0.10)' : 'rgba(59,130,246,0.10)') : 'transparent',
              }}>
              <input
                type="checkbox" checked={active}
                onChange={() => toggle(opt.id)}
                style={{ marginTop: 2, accentColor: opt.urgent ? '#ef4444' : '#3b82f6' }}
              />
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: 11, fontWeight: 600,
                  color: active ? (opt.urgent ? '#ef4444' : '#3b82f6') : 'var(--text-primary)',
                }}>
                  {opt.label}
                  {opt.urgent && <span style={{ marginLeft: 4, fontSize: 9, color: '#ef4444' }}>● incidente</span>}
                </div>
                <div style={{ fontSize: 9, color: 'var(--text-muted)', lineHeight: 1.3 }}>
                  {opt.desc}
                </div>
              </div>
            </label>
          )
        })}

        {/* Sub-config merodeo */}
        {flags.merodeo && (
          <div style={{
            marginTop: 4, padding: '6px 8px', borderRadius: 4,
            background: 'rgba(245,158,11,0.08)',
            border: '1px solid rgba(245,158,11,0.3)',
            fontSize: 10,
          }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: 'var(--text-muted)' }}>Segundos:</span>
              <input
                type="number" min={2} max={120} value={loitSecs}
                onChange={e => setLoitSecs(Math.max(2, Number(e.target.value) || 5))}
                style={{
                  width: 50, padding: '2px 4px', fontSize: 11,
                  background: 'var(--bg-root)', color: 'var(--text-primary)',
                  border: '1px solid var(--border)', borderRadius: 3,
                }}
              />
              <span style={{ color: 'var(--text-muted)', fontSize: 9 }}>(default 5s)</span>
            </label>
          </div>
        )}

        {/* Sub-config perímetro */}
        {flags.perimetro && (
          <div style={{
            marginTop: 4, padding: '6px 8px', borderRadius: 4,
            background: 'rgba(239,68,68,0.08)',
            border: '1px solid rgba(239,68,68,0.3)',
            fontSize: 10,
          }}>
            <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
              {['line', 'polygon'].map(m => (
                <button
                  key={m}
                  onClick={() => setProtLine(p => ({ ...p, mode: m }))}
                  style={{
                    flex: 1, padding: '3px 6px', fontSize: 10, fontWeight: 600,
                    background: protLine.mode === m ? '#ef4444' : 'var(--bg-muted)',
                    color: protLine.mode === m ? '#fff' : 'var(--text-muted)',
                    border: 'none', borderRadius: 3, cursor: 'pointer',
                  }}>
                  {m === 'line' ? 'Línea vertical' : 'Polígono (dibujar)'}
                </button>
              ))}
            </div>
            {protLine.mode === 'line' && (
              <>
                <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                  Posición X: <strong>{Math.round((protLine.x_rel || 0.5) * 100)}%</strong> · dirección{' '}
                  <select
                    value={protLine.direction || 'right_to_left'}
                    onChange={e => setProtLine(p => ({ ...p, direction: e.target.value }))}
                    style={{ fontSize: 10, padding: '1px 3px' }}>
                    <option value="right_to_left">→ Der a Izq</option>
                    <option value="left_to_right">← Izq a Der</option>
                  </select>
                </div>
                <input
                  type="range" min={0.05} max={0.95} step={0.01}
                  value={protLine.x_rel || 0.5}
                  onChange={e => setProtLine(p => ({ ...p, x_rel: Number(e.target.value) }))}
                  style={{ width: '100%', marginTop: 2 }}
                />
              </>
            )}
            {protLine.mode === 'polygon' && (
              <div style={{ fontSize: 10, color: 'var(--text-muted)', textAlign: 'center', padding: '6px 0' }}>
                Dibujado de polígono en próxima versión.<br/>
                Por ahora usa modo línea.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── CamerasConfigPanel ──────────────────────────────────────────────────────

export default function CamerasConfigPanel({ nvr, token, onClose, onSaved }) {
  const [channels, setChannels] = useState([])
  const [edgeNode, setEdgeNode] = useState(null)
  const [camerasMap, setCamerasMap] = useState({})  // channel → cam config
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [savedAt, setSavedAt] = useState(null)

  // Cargar canales del NVR + buscar el edge_node del site
  useEffect(() => {
    if (!nvr || !token) return
    const auth = { headers: { Authorization: `Bearer ${token}` } }
    setLoading(true)
    Promise.all([
      fetch(`/api/nvr/${nvr.id}/channels`, auth).then(r => r.ok ? r.json() : []),
      fetch('/api/edge/nodes', auth).then(r => r.ok ? r.json() : []),
    ]).then(([chs, nodes]) => {
      setChannels(Array.isArray(chs) ? chs : [])
      const node = (nodes || []).find(n => n.site_id === nvr.site_id)
      if (!node) {
        setError(`Este NVR está en el sitio ${nvr.site_id} pero no encontré un nodo edge asociado. Crealo primero desde Settings → Edge Nodes.`)
        setLoading(false)
        return
      }
      setEdgeNode(node)
      // Cargar remote_config del nodo
      fetch(`/api/edge/config/${node.id}`, auth)
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          const cams = data?.config?.cameras || []
          const map = {}
          for (const c of cams) map[c.channel] = c
          setCamerasMap(map)
          setLoading(false)
        })
        .catch(() => { setLoading(false) })
    }).catch(e => { setError(String(e)); setLoading(false) })
  }, [nvr, token])

  const handleCameraChange = useCallback((channel, newCamConfig) => {
    setCamerasMap(prev => ({ ...prev, [channel]: newCamConfig }))
  }, [])

  const save = async () => {
    if (!edgeNode) return
    setSaving(true); setError(null)
    try {
      // Leer config actual completa, modificar solo cameras[]
      const auth = { headers: { Authorization: `Bearer ${token}` } }
      const r = await fetch(`/api/edge/config/${edgeNode.id}`, auth)
      const data = await r.json()
      const currentCfg = data?.config || {}
      const camerasArr = Object.values(camerasMap).sort((a, b) => a.channel - b.channel)
      const nextCfg = { ...currentCfg, cameras: camerasArr }

      const putR = await fetch(`/api/edge/config/${edgeNode.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(nextCfg),
      })
      if (!putR.ok) {
        const t = await putR.text()
        throw new Error(`PUT falló (${putR.status}): ${t}`)
      }
      setSavedAt(new Date())
      onSaved?.()
    } catch (e) {
      setError(String(e.message || e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}>
      <div style={{
        width: '95vw', maxWidth: 1400, height: '90vh',
        background: 'var(--bg-root)', border: '1px solid var(--border)',
        borderRadius: 10, display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '12px 18px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'var(--bg-surface)',
        }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>
              Canales y analíticas — {nvr?.name}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              {(nvr?.brand || '').toUpperCase()} · {nvr?.host} · {channels.length} canales
              {edgeNode && <span> · edge node #{edgeNode.id}</span>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {savedAt && (
              <span style={{ fontSize: 11, color: '#10b981' }}>
                ✓ Guardado {savedAt.toLocaleTimeString()}
              </span>
            )}
            <button
              onClick={save}
              disabled={saving || loading || !edgeNode}
              style={{
                padding: '7px 16px', fontSize: 12, fontWeight: 700,
                background: 'var(--accent)', color: '#fff', border: 'none',
                borderRadius: 5, cursor: saving ? 'wait' : 'pointer',
                opacity: (saving || loading || !edgeNode) ? 0.6 : 1,
              }}>
              {saving ? 'Guardando…' : 'Guardar y enviar al edge'}
            </button>
            <button
              onClick={onClose}
              style={{
                padding: '7px 12px', fontSize: 12,
                background: 'transparent', color: 'var(--text-muted)',
                border: '1px solid var(--border)', borderRadius: 5, cursor: 'pointer',
              }}>
              Cerrar
            </button>
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div style={{
            padding: '8px 18px', background: 'rgba(239,68,68,0.15)',
            color: '#ef4444', fontSize: 12, borderBottom: '1px solid rgba(239,68,68,0.3)',
          }}>
            {error}
          </div>
        )}

        {/* Grid de canales */}
        <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
              Cargando canales y configuración del nodo edge…
            </div>
          ) : channels.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
              Sin canales disponibles. Verifica que el NVR responda.
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: 14,
            }}>
              {channels.map(ch => (
                <CameraCard
                  key={ch.channel}
                  channel={ch.channel}
                  channelName={camerasMap[ch.channel]?.name || ch.name}
                  snapshotUrl={`/api/nvr/${nvr.id}/snapshot/${ch.channel}?token=${encodeURIComponent(token)}`}
                  camConfig={camerasMap[ch.channel] || { channel: ch.channel, name: ch.name, analytics: [] }}
                  onChange={handleCameraChange}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer info */}
        <div style={{
          padding: '8px 18px', borderTop: '1px solid var(--border)',
          fontSize: 10, color: 'var(--text-muted)',
          background: 'var(--bg-surface)',
        }}>
          Los cambios se aplican al daemon del edge en su próximo refresh (típico 30-60s).
          Las analíticas marcadas con <span style={{ color: '#ef4444' }}>● incidente</span> generan
          alerta crítica e incidente urgente inmediato al detectar.
        </div>
      </div>
    </div>
  )
}
